import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface PartnerInfo {
  id: UUID;
  name: string;
}

export interface TransactionInfo {
  id: UUID;
  transaction_type: string;
  status: string;
  effective_date: string | null;
  description: string | null;
}

// Nested types for detailed operation response
export interface UnitInfo {
  id: UUID;
  name: string;
  symbol: string;
}

export interface ProductInfo {
  id: UUID;
  name: string;
  short_name: string | null;
  sku: string | null;
}

export interface LotInfo {
  id: UUID;
  lot_number: string;
}

export interface LocationInfo {
  id: UUID;
  name: string;
}

export interface StockInfo {
  id: UUID;
  lot_id: UUID | null;
  lot: LotInfo | null;
  location_id: UUID | null;
  location: LocationInfo | null;
}

export interface FlowDetail {
  id: UUID;
  product_id: UUID;
  product: ProductInfo | null;
  unit_id: UUID | null;
  unit: UnitInfo | null;
  stock_id: UUID | null;
  stock: StockInfo | null;
  partner_id: UUID | null;
  partner: PartnerInfo | null;
  direction: string;
  flow_type: string;
  quantity: number;
  weight: number;
  price: number | null;
  provisional: boolean;
  order_date: string | null;
  delivery_date: string | null;
  comment: string | null;
  hidden_comment: string | null;
  flow_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface TransferStockInfo {
  id: UUID;
  lot: LotInfo | null;
  location: LocationInfo | null;
  product: ProductInfo | null;
}

export interface TransferDetail {
  id: UUID;
  transfer_type: string;
  status: string;
  source_stock_id: UUID | null;
  source_stock: TransferStockInfo | null;
  destination_stock_id: UUID | null;
  destination_stock: TransferStockInfo | null;
  source_quantity: number;
  source_weight: number;
  destination_quantity: number;
  destination_weight: number;
  price: number | null;
  transfer_metadata: Record<string, any>;
  created_at: string;
}

export interface OperationDocument {
  id: UUID;
  company_id: UUID;
  name: string;
  url: string;
  document_type: string | null;
  reference_type: string;
  reference_id: UUID;
  document_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface OperationFee {
  id: UUID;
  company_id: UUID;
  fee_type: string;
  amount: string;
  description: string | null;
  reference_type: string;
  reference_id: UUID;
  fee_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface OperationFeesResponse {
  fees: OperationFee[];
  operation_id: UUID;
  total: string;
}

export interface TransactionDetail {
  id: UUID;
  transaction_type: string;
  status: string;
  date: string | null;
  effective_date: string | null;
  description: string | null;
  transaction_metadata: Record<string, any>;
  partner_id: UUID | null;
  partner: PartnerInfo | null;
  created_at: string;
  updated_at: string;
  flows: FlowDetail[];
  transfers: TransferDetail[];
}

export interface OperationDetailResponse extends OperationResponse {
  transactions: TransactionDetail[];
}

export interface OperationResponse {
  id: UUID;
  company_id: UUID;
  partner_id: UUID | null;
  partner: PartnerInfo | null;
  operation_type: string;
  status: string;
  reference_number: string | null;
  date: string | null;
  effective_date: string | null;
  description: string | null;
  operation_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  // Computed fields
  transaction_count: number;
  flow_count: number;
  total_amount: number | null;
}

export interface OperationWithTransactionsResponse extends OperationResponse {
  transactions: TransactionInfo[];
}

export interface OperationCreate {
  operation_type: string;
  partner_id?: UUID | null;
  reference_number?: string | null;
  date?: string | null;
  effective_date?: string | null;
  description?: string | null;
  operation_metadata?: Record<string, any>;
}

// Types for atomic full operation creation
export interface PendingLotCreate {
  temp_id: string;
  lot_number: string;
  supplier_id?: UUID | null;
  tracking_number?: string | null;
}

export interface PendingStockCreate {
  temp_id: string;
  product_id: UUID;
  lot_temp_id: string;
  partner_id?: UUID | null;
  location_id?: UUID | null;
}

export interface FlowLineCreate {
  product_id: UUID;
  unit_id?: UUID | null; // Optional - backend will resolve from product's default unit if not provided
  stock_id?: UUID | null;
  stock_temp_id?: string | null;
  quantity: number;
  weight?: number;
  price?: number | null;
  comment?: string | null;
  hidden_comment?: string | null;
  metadata?: Record<string, any>;
}

export interface TransactionCreateData {
  description?: string | null;
  metadata?: Record<string, any>;
  lines: FlowLineCreate[];
}

export interface FullOperationCreate {
  operation_type: string;
  partner_id?: UUID | null;
  reference_number?: string | null;
  date?: string | null;
  effective_date?: string | null;
  description?: string | null;
  operation_metadata?: Record<string, any>;
  pending_lots: PendingLotCreate[];
  pending_stocks: PendingStockCreate[];
  transactions: TransactionCreateData[];
  save_as_draft?: boolean;
}

export interface OperationUpdate {
  operation_type?: string;
  partner_id?: UUID | null;
  reference_number?: string | null;
  date?: string | null;
  effective_date?: string | null;
  description?: string | null;
  operation_metadata?: Record<string, any>;
}

export interface OperationFilters {
  operation_type?: string;
  status?: string;
  partner_id?: UUID;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default class APIOperations extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  list = async (filters?: OperationFilters): Promise<OperationResponse[]> => {
    const params = new URLSearchParams();
    if (filters?.operation_type)
      params.append("operation_type", filters.operation_type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.partner_id) params.append("partner_id", filters.partner_id);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.page_size) params.append("size", filters.page_size.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/operations/${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  listPaginated = async (
    filters?: OperationFilters,
  ): Promise<{ items: OperationResponse[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.operation_type)
      params.append("operation_type", filters.operation_type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.partner_id) params.append("partner_id", filters.partner_id);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.page_size) params.append("size", filters.page_size.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/operations/${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      const data = await res.json();
      // Backend returns total count in X-Total-Count header
      const total = parseInt(
        res.headers.get("X-Total-Count") || String(data.length),
        10,
      );
      return { items: data, total };
    }
    throw new Error("Bad content type");
  };

  get = async (id: UUID): Promise<OperationResponse> => {
    const res = await this._fetch({
      url: `/operations/${id}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getWithTransactions = async (
    id: UUID,
  ): Promise<OperationWithTransactionsResponse> => {
    const res = await this._fetch({
      url: `/operations/${id}/with-transactions`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getDetails = async (id: UUID): Promise<OperationDetailResponse> => {
    const res = await this._fetch({
      url: `/operations/${id}/details`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  create = async (data: OperationCreate): Promise<OperationResponse> => {
    const res = await this._fetch({
      url: `/operations/`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a complete operation with lots, stocks, transactions, and flows atomically.
   * This ensures all entities are created in a single transaction - if any step fails,
   * everything is rolled back.
   */
  createFull = async (
    data: FullOperationCreate,
  ): Promise<OperationResponse> => {
    const res = await this._fetch({
      url: `/operations/full`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  update = async (
    id: UUID,
    data: OperationUpdate,
  ): Promise<OperationResponse> => {
    const res = await this._fetch({
      url: `/operations/${id}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  delete = async (id: UUID): Promise<void> => {
    await this._fetch({
      url: `/operations/${id}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  complete = async (id: UUID): Promise<OperationResponse> => {
    const res = await this._fetch({
      url: `/operations/${id}/complete`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  cancel = async (id: UUID): Promise<OperationResponse> => {
    const res = await this._fetch({
      url: `/operations/${id}/cancel`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Document management
  uploadDocuments = async (
    operationId: UUID,
    files: File[],
  ): Promise<{
    message: string;
    files: OperationDocument[];
    operation_id: UUID;
  }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const res = await this._fetch({
      url: `/operations/${operationId}/documents/upload`,
      method: "POST",
      body: formData,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getDocuments = async (
    operationId: UUID,
  ): Promise<{ documents: OperationDocument[]; operation_id: UUID }> => {
    const res = await this._fetch({
      url: `/operations/${operationId}/documents`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  deleteDocument = async (
    operationId: UUID,
    documentId: UUID,
  ): Promise<{ message: string; document_id: UUID }> => {
    const res = await this._fetch({
      url: `/operations/${operationId}/documents/${documentId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Download a document from an operation (for preview/download)
  downloadDocument = async (
    operationId: UUID,
    fileUrl: string,
  ): Promise<Blob> => {
    const res = await this._fetch({
      url: `/operations/${operationId}/documents/download`,
      method: "GET",
      params: { file_url: fileUrl },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  // Fee management
  getFees = async (operationId: UUID): Promise<OperationFeesResponse> => {
    const res = await this._fetch({
      url: `/operations/${operationId}/fees`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
